import { enhanceChartDropdown } from "./chart-dropdown.js";
import { QuoteMediaBrowserOptionsClient, validateOptionBrowserGrant, optionExpirations,
  selectOptionStrikes, buildBrowserOptionChain, optionError } from "../data/quotemedia-options-client.js";

const DEFAULT_LIMIT = 50;
const SEARCH_DEBOUNCE_MS = 150;
const ONE_CHARACTER_SEARCH_DEBOUNCE_MS = 350;
const LOADING_INDICATOR_DELAY_MS = 150;
const FOCUSABLE_SELECTOR = [
  "button:not([disabled]):not([hidden])",
  "input:not([disabled]):not([hidden])",
  "[tabindex]:not([tabindex='-1']):not([hidden])"
].join(",");

export class SymbolSearchRepository {
  constructor(endpoint = "/api/v1/symbols/search") {
    this.endpoint = endpoint;
  }

  async search({ query = "", securityType = null, limit = DEFAULT_LIMIT, signal } = {}) {
    const parameters = new URLSearchParams({
      query: String(query),
      limit: String(limit)
    });
    if (securityType) parameters.set("securityType", securityType);
    const response = await fetch(`${this.endpoint}?${parameters}`, {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
      signal
    });
    if (!response.ok) {
      const error = new Error(`Symbol search failed with status ${response.status}.`);
      error.name = "SymbolSearchRequestError";
      error.status = response.status;
      throw error;
    }

    const payload = await response.json();
    return {
      results: Array.isArray(payload.results)
        ? payload.results.map(normalizeInstrument).filter(Boolean)
        : [],
      facets: Array.isArray(payload.facets)
        ? payload.facets
            .map((facet) => ({
              securityType: String(facet.securityType || ""),
              count: Number(facet.count) || 0
            }))
            .filter((facet) => facet.securityType)
        : [],
      total: Number(payload.total) || 0
    };
  }
}

export class OptionChainRepository {
  constructor(endpoint = null, { browserRequests = true, browserClient = new QuoteMediaBrowserOptionsClient(),
    csrfToken = globalThis.document?.querySelector?.('meta[name="csrf-token"]')?.content || null } = {}) {
    this.endpoint = null;
    this.browserRequests = browserRequests;
    this.browserClient = browserClient;
    this.csrfToken = csrfToken;
    if (endpoint) this.setEndpoint(endpoint);
  }

  setEndpoint(endpoint) {
    const normalized = String(endpoint || "").trim().replace(/\/+$/, "");
    if (!normalized.startsWith("/") || normalized.startsWith("//") || normalized.includes("\\")) {
      throw new TypeError("Option data endpoint must be an application-relative URL.");
    }
    this.endpoint = normalized;
  }

  async expirations(underlyingInstrumentId, { signal } = {}) {
    this.#requireEndpoint();
    if (this.browserRequests) {
      return this.#withGrant(underlyingInstrumentId, signal, async grant => {
        if (grant.transport === "Server") return this.#get(
          `${this.endpoint}/${encodeURIComponent(underlyingInstrumentId)}/expirations`, signal);
        return optionExpirations(await this.browserClient.chain(grant, { signal }), grant);
      });
    }
    return this.#get(
      `${this.endpoint}/${encodeURIComponent(underlyingInstrumentId)}/expirations`,
      signal
    );
  }

  async chain({
    underlyingInstrumentId,
    expiration,
    strikeRange = "StrikesAroundMarket",
    strikes = 10,
    side = "Both",
    minimumStrike = null,
    maximumStrike = null,
    signal
  }) {
    this.#requireEndpoint();
    if (this.browserRequests) {
      const request = { underlyingInstrumentId, expiration, strikeRange, strikes, side, minimumStrike, maximumStrike, signal };
      return this.#withGrant(underlyingInstrumentId, signal, async grant => {
        if (grant.transport === "Server") return this.#serverChain(request);
        const snapshot = await this.browserClient.chain(grant, { expiration, side, signal });
        if (snapshot.underlying.last === null || snapshot.underlying.change === null ||
            snapshot.underlying.changePercent === null || snapshot.underlying.quoteState === "Unavailable") {
          try {
            const quote = await this.browserClient.underlyingQuote(grant, { signal });
            if (quote.last !== null) snapshot.underlying = quote;
          } catch (error) {
            if (signal?.aborted || error.reason === "provider_auth_rejected") throw error;
            // A missing stock quote must not hide otherwise usable option rows.
          }
        }
        const contracts = snapshot.contracts.filter(item => item.expiration === expiration &&
          (side === "Both" || item.right === side));
        const allStrikes = [...new Set(contracts.map(item => item.strike))].sort((a, b) => a - b);
        let atTheMoney = snapshot.underlying.last === null || !allStrikes.length ? null :
          allStrikes.reduce((best, value) => Math.abs(value - snapshot.underlying.last) < Math.abs(best - snapshot.underlying.last) ? value : best);
        if (atTheMoney === null && allStrikes.length) {
          try {
            const near = await this.browserClient.chain(grant, { expiration, side, money: "at", signal });
            atTheMoney = near.contracts.filter(item => item.expiration === expiration && allStrikes.includes(item.strike))
              .map(item => item.strike).sort((a, b) => a - b)[0] ?? null;
          } catch (error) {
            if (signal?.aborted || error.status !== 502 || !["All", "Custom"].includes(strikeRange)) throw error;
          }
        }
        const selected = selectOptionStrikes(contracts, request, atTheMoney);
        const identities = await this.#resolveContracts(underlyingInstrumentId, selected, signal);
        return buildBrowserOptionChain(snapshot, selected, [], identities, request, grant, atTheMoney);
      });
    }
    return this.#serverChain({ underlyingInstrumentId, expiration, strikeRange, strikes, side, minimumStrike, maximumStrike, signal });
  }

  async rowDetails(chain, row, { signal } = {}) {
    this.#requireEndpoint();
    if (!this.browserRequests) return row;
    const underlyingInstrumentId = chain.underlying.instrument.instrumentId;
    return this.#withGrant(underlyingInstrumentId, signal, async grant => {
      if (grant.transport === "Server") return row;
      const entries = [row.call, row.put].filter(item => item?.contract);
      const selected = entries.map(({ contract, quote }) => ({
        symbol: contract.canonicalSymbol, expiration: contract.expiration, strike: contract.strike,
        right: contract.right, underlyingSymbol: grant.underlying.instrument.providerSymbol, quote
      }));
      const details = await this.browserClient.quotes(grant, selected.map(item => item.symbol), signal);
      return buildBrowserOptionChain({ underlying: chain.underlying }, selected, details,
        entries.map(item => item.contract), { underlyingInstrumentId, expiration: chain.expiration.date },
        grant, chain.atTheMoneyStrike).rows[0] || row;
    });
  }

  async #serverChain({ underlyingInstrumentId, expiration, strikeRange, strikes, side, minimumStrike, maximumStrike, signal }) {
    const parameters = new URLSearchParams({
      expiration,
      strikeRange,
      strikes: String(strikes),
      side
    });
    if (minimumStrike !== null) parameters.set("minimumStrike", String(minimumStrike));
    if (maximumStrike !== null) parameters.set("maximumStrike", String(maximumStrike));
    return this.#get(
      `${this.endpoint}/${encodeURIComponent(underlyingInstrumentId)}/chain?${parameters}`,
      signal
    );
  }

  async #withGrant(underlyingInstrumentId, signal, load) {
    const url = `${this.endpoint}/${encodeURIComponent(underlyingInstrumentId)}/request-grant`;
    const grant = await this.#post(url, {}, signal);
    if (grant.transport === "Browser") validateOptionBrowserGrant(grant);
    else if (grant.transport !== "Server") throw optionError("Option data transport is unavailable.", 502);
    try { return await load(grant); }
    catch (error) {
      if (signal?.aborted || grant.transport !== "Browser" || error.reason !== "provider_auth_rejected") throw error;
      const refreshed = await this.#post(url, { rejectedSid: grant.sid }, signal);
      validateOptionBrowserGrant(refreshed);
      return load(refreshed);
    }
  }

  async #resolveContracts(underlyingInstrumentId, selected, signal) {
    const contracts = [];
    for (let first = 0; first < selected.length; first += 128) {
      contracts.push(...await this.#post(`${this.endpoint}/${encodeURIComponent(underlyingInstrumentId)}/contracts/resolve`,
        selected.slice(first, first + 128).map(item => ({ providerSymbol: item.symbol, exchangeCode: item.exchangeCode })), signal));
    }
    return contracts;
  }

  async #post(url, body, signal) {
    const response = await fetch(url, {
      method: "POST", credentials: "same-origin", cache: "no-store", signal,
      headers: { Accept: "application/json", "Content-Type": "application/json",
        ...(this.csrfToken ? { "X-CSRF-TOKEN": this.csrfToken } : {}) }, body: JSON.stringify(body)
    });
    if (!response.ok) {
      let reason = null;
      try {
        const problem = await response.json();
        if (["active_entitlement_required", "feed_not_mapped", "capability-disabled", "active_product_subscription_required",
          "commercial_chart_access_required", "provider_user_inactive"].includes(problem?.reason)) reason = problem.reason;
      } catch { /* Retain the safe status-only error for non-JSON responses. */ }
      throw optionError("Options authorization or contract lookup failed.", response.status, reason);
    }
    return response.json();
  }

  async #get(url, signal) {
    const response = await fetch(url, {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
      signal
    });
    if (!response.ok) {
      const error = new Error(`Option data failed with status ${response.status}.`);
      error.name = "OptionChainRequestError";
      error.status = response.status;
      throw error;
    }
    return response.json();
  }

  #requireEndpoint() {
    if (!this.endpoint) {
      throw new TypeError("Option data requires a product-qualified endpoint.");
    }
  }
}

export class SymbolSessionStore {
  constructor() {
    this.favoriteIds = new Set();
    this.recentIds = [];
  }

  isFavorite(instrumentId) {
    return this.favoriteIds.has(instrumentId);
  }

  toggleFavorite(instrumentId) {
    if (this.favoriteIds.delete(instrumentId)) return false;
    this.favoriteIds.add(instrumentId);
    return true;
  }

  recordRecent(instrumentId) {
    this.recentIds = [
      instrumentId,
      ...this.recentIds.filter((candidate) => candidate !== instrumentId)
    ].slice(0, 12);
  }

  favorites() {
    return new Set(this.favoriteIds);
  }

  recents() {
    return [...this.recentIds];
  }
}

export class SymbolSearch {
  constructor({
    element,
    repository = new SymbolSearchRepository(),
    optionRepository = new OptionChainRepository(),
    sessionStore = new SymbolSessionStore(),
    getCurrentInstrumentId = () => null,
    getOpenInstrumentIds = () => [],
    onBeforeOpen = () => {},
    onClose = () => {},
    onSelect = () => true,
    onAnnounce = () => {}
  }) {
    if (!(element instanceof HTMLElement)) {
      throw new TypeError("Symbol Search requires a root element.");
    }

    this.element = element;
    this.repository = repository;
    this.optionRepository = optionRepository;
    this.sessionStore = sessionStore;
    this.getCurrentInstrumentId = getCurrentInstrumentId;
    this.getOpenInstrumentIds = getOpenInstrumentIds;
    this.onBeforeOpen = onBeforeOpen;
    this.onClose = onClose;
    this.onSelect = onSelect;
    this.selectionHandler = onSelect;
    this.onAnnounce = onAnnounce;
    this.dialog = requiredElement("[data-symbol-search-dialog]", element);
    this.input = requiredElement("[data-symbol-search-input]", element);
    this.clearButton = requiredElement("[data-symbol-search-clear]", element);
    this.closeButton = requiredElement("[data-symbol-search-close]", element);
    this.backdrop = requiredElement("[data-symbol-search-backdrop]", element);
    this.filters = requiredElement("[data-symbol-search-filters]", element);
    this.list = requiredElement("[data-symbol-search-results]", element);
    this.status = requiredElement("[data-symbol-search-status]", element);
    this.loading = requiredElement("[data-symbol-search-loading]", element);
    this.directoryView = requiredElement("[data-symbol-search-directory-view]", element);
    this.optionsInstruction = requiredElement(
      "[data-symbol-search-options-instruction]",
      element
    );
    this.mode = element.querySelector("[data-symbol-search-mode]");
    this.chainView = requiredElement("[data-option-chain-view]", element);
    this.chainBackButton = requiredElement("[data-option-chain-back]", element);
    this.chainCloseButton = requiredElement("[data-option-chain-close]", element);
    this.chainSymbol = requiredElement("[data-option-chain-symbol]", element);
    this.chainName = requiredElement("[data-option-chain-name]", element);
    this.chainPrice = requiredElement("[data-option-chain-price]", element);
    this.chainChange = requiredElement("[data-option-chain-change]", element);
    // Older compiled/cached views can still contain the retired header metadata.
    element.querySelector("[data-option-chain-quote-state]")?.remove();
    element.querySelector("[data-option-chain-updated]")?.remove();
    this.chainExpiration = requiredElement("[data-option-chain-expiration]", element);
    element.querySelector(".option-chain-dte")?.remove();
    this.chainRange = requiredElement("[data-option-chain-range]", element);
    this.chainCustomRange = requiredElement("[data-option-chain-custom-range]", element);
    this.chainMinimum = requiredElement("[data-option-chain-minimum]", element);
    this.chainMaximum = requiredElement("[data-option-chain-maximum]", element);
    this.chainSide = requiredElement("[data-option-chain-side]", element);
    this.chainRefresh = requiredElement("[data-option-chain-refresh]", element);
    this.chainState = requiredElement("[data-option-chain-state]", element);
    this.chainScroll = requiredElement("[data-option-chain-scroll]", element);
    this.chainTable = requiredElement("[data-option-chain-table]", element);
    this.chainRows = requiredElement("[data-option-chain-rows]", element);
    this.chainStatus = requiredElement("[data-option-chain-status]", element);
    this.results = [];
    this.facets = [];
    this.selectedIndex = -1;
    this.securityType = null;
    this.opened = false;
    this.requestSequence = 0;
    this.requestController = null;
    this.searchTimer = null;
    this.loadingTimer = null;
    this.returnFocus = null;
    this.currentQuery = "";
    this.restoreFilterFocus = false;
    this.view = "directory";
    this.chainUnderlying = null;
    this.chainExpirations = [];
    this.chainResponse = null;
    this.chainRequestSequence = 0;
    this.chainRequestController = null;
    this.expirationRequestController = null;
    this.expandedStrikes = new Set();
    this.optionDetailRequests = new Map();
    this.loadedOptionDetails = new Set();
    this.focusedStrikeIndex = -1;
    this.focusedSide = "Call";
    this.chainExpirationDropdown = null;
    this.chainRangeDropdown = enhanceChartDropdown(this.chainRange);

    this.input.addEventListener("input", () => this.scheduleSearch());
    this.input.addEventListener("keydown", (event) => this.handleResultNavigation(event));
    this.clearButton.addEventListener("click", () => this.clearQuery());
    this.closeButton.addEventListener("click", () => this.close());
    this.backdrop.addEventListener("pointerdown", () => this.close());
    this.list.addEventListener("keydown", (event) => this.handleResultNavigation(event));
    this.dialog.addEventListener("keydown", (event) => this.handleDialogKeydown(event));
    this.chainBackButton.addEventListener("click", () => this.showDirectoryView());
    this.chainCloseButton.addEventListener("click", () => this.close());
    this.chainExpiration.addEventListener("change", () => this.handleExpirationChange());
    this.chainRange.addEventListener("change", () => this.handleRangeChange());
    this.chainSide.addEventListener("change", () => this.loadChain());
    this.chainRefresh.addEventListener("click", () => this.loadChain({ preserveRows: true }));
    this.chainMinimum.addEventListener("change", () => this.loadChain());
    this.chainMaximum.addEventListener("change", () => this.loadChain());
    this.chainRows.addEventListener("keydown", (event) => this.handleChainKeydown(event));
  }

  setOptionEndpoint(endpoint) {
    this.optionRepository.setEndpoint(endpoint);
  }

  open({
    query = "",
    trigger = null,
    onSelect = null,
    modeLabel = "",
    selectQuery = true
  } = {}) {
    this.onBeforeOpen();
    this.selectionHandler = typeof onSelect === "function" ? onSelect : this.onSelect;
    if (this.mode instanceof HTMLElement) {
      this.mode.textContent = modeLabel;
      this.mode.hidden = !modeLabel;
    }
    this.returnFocus = trigger instanceof HTMLElement
      ? trigger
      : document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    this.opened = true;
    this.element.hidden = false;
    this.element.dataset.state = "open";
    this.showDirectoryView({ restoreState: false });
    this.securityType = null;
    this.currentQuery = String(query);
    this.input.value = this.currentQuery;
    this.clearButton.hidden = this.currentQuery.length === 0;
    this.input.setAttribute("aria-expanded", "true");
    this.selectedIndex = -1;
    this.syncSelection();
    this.performSearch();
    requestAnimationFrame(() => {
      this.input.focus({ preventScroll: true });
      if (selectQuery) {
        this.input.select();
      } else {
        const end = this.input.value.length;
        this.input.setSelectionRange(end, end);
      }
    });
  }

  close({ restoreFocus = true, focusTarget = null } = {}) {
    if (!this.opened) return;
    this.opened = false;
    this.requestSequence += 1;
    this.requestController?.abort();
    this.requestController = null;
    clearTimeout(this.searchTimer);
    this.searchTimer = null;
    clearTimeout(this.loadingTimer);
    this.loadingTimer = null;
    this.setLoading(false);
    this.cancelOptionRequests();
    this.element.hidden = true;
    delete this.element.dataset.state;
    this.input.setAttribute("aria-expanded", "false");
    this.input.removeAttribute("aria-activedescendant");
    this.selectionHandler = this.onSelect;
    if (this.mode instanceof HTMLElement) {
      this.mode.textContent = "";
      this.mode.hidden = true;
    }
    this.onClose();
    if (!restoreFocus) return;
    const target = focusTarget instanceof HTMLElement ? focusTarget : this.returnFocus;
    target?.focus({ preventScroll: true });
  }

  destroy() {
    this.close({ restoreFocus: false });
    this.chainExpirationDropdown?.destroy();
    this.chainRangeDropdown?.destroy();
  }

  scheduleSearch() {
    this.currentQuery = this.input.value;
    this.clearButton.hidden = this.currentQuery.length === 0;
    this.selectedIndex = -1;
    this.syncSelection();
    clearTimeout(this.searchTimer);
    const delay = normalizeText(this.currentQuery).length === 1
      ? ONE_CHARACTER_SEARCH_DEBOUNCE_MS
      : SEARCH_DEBOUNCE_MS;
    this.searchTimer = setTimeout(() => this.performSearch(), delay);
  }

  async performSearch() {
    if (!this.opened) return;
    clearTimeout(this.searchTimer);
    this.searchTimer = null;
    this.requestController?.abort();
    clearTimeout(this.loadingTimer);
    const controller = new AbortController();
    this.requestController = controller;
    const sequence = ++this.requestSequence;
    this.loadingTimer = setTimeout(() => {
      if (this.opened && !controller.signal.aborted && sequence === this.requestSequence) {
        this.setLoading(true);
      }
    }, LOADING_INDICATOR_DELAY_MS);

    try {
      const response = await this.repository.search({
        query: this.currentQuery,
        securityType: this.securityType,
        limit: DEFAULT_LIMIT,
        signal: controller.signal
      });
      if (!this.opened || controller.signal.aborted || sequence !== this.requestSequence) return;
      clearTimeout(this.loadingTimer);
      this.loadingTimer = null;
      this.facets = response.facets;
      this.results = rankInstruments(response.results, this.currentQuery, {
        favoriteIds: this.sessionStore.favorites(),
        recentIds: this.sessionStore.recents(),
        currentInstrumentId: this.getCurrentInstrumentId(),
        openInstrumentIds: this.getOpenInstrumentIds()
      });
      this.selectedIndex = this.results.length > 0 ? 0 : -1;
      this.renderFilters();
      this.renderResults();
      this.setLoading(false);
      this.announceResults(response.total);
    } catch (error) {
      if (error?.name === "AbortError" || sequence !== this.requestSequence || !this.opened) return;
      clearTimeout(this.loadingTimer);
      this.loadingTimer = null;
      this.results = [];
      this.selectedIndex = -1;
      this.setLoading(false);
      this.renderFailure();
    }
  }

  renderFilters() {
    const available = [
      { value: null, label: "All", count: directoryFacetTotal(this.facets) },
      ...this.facets.map((facet) => ({
        value: facet.securityType,
        label: securityTypeLabel(facet.securityType),
        count: facet.count
      }))
    ];
    this.filters.replaceChildren(...available.map((filter, index) => {
      const button = document.createElement("button");
      button.type = "button";
      button.setAttribute("role", "tab");
      button.dataset.securityType = filter.value || "";
      button.setAttribute("aria-selected", String(filter.value === this.securityType));
      button.tabIndex = filter.value === this.securityType ? 0 : -1;
      button.append(
        document.createTextNode(filter.label),
        createCount(String(filter.count))
      );
      button.addEventListener("click", () => this.selectFilter(filter.value, button));
      button.addEventListener("keydown", (event) => this.handleFilterKeydown(event, index, available));
      return button;
    }));
    this.optionsInstruction.hidden = this.securityType !== "Option";
    if (this.restoreFilterFocus) {
      this.restoreFilterFocus = false;
      this.filters
        .querySelector('[role="tab"][aria-selected="true"]')
        ?.focus({ preventScroll: true });
    }
  }

  renderResults() {
    this.list.replaceChildren();
    this.list.removeAttribute("aria-busy");
    if (this.results.length === 0) {
      const empty = document.createElement("div");
      empty.className = "symbol-search-empty";
      empty.setAttribute("role", "status");
      empty.innerHTML = this.currentQuery
        ? "<strong>No symbols found</strong><span>Try another ticker, company, or exchange.</span>"
        : "<strong>No recent or open symbols</strong><span>Start typing to search the supported catalog.</span>";
      this.list.append(empty);
      this.syncActiveDescendant();
      return;
    }

    const currentId = this.getCurrentInstrumentId();
    const openIds = new Set(this.getOpenInstrumentIds());
    const query = this.currentQuery;
    for (const [index, instrument] of this.results.entries()) {
      const row = document.createElement("div");
      row.className = "symbol-search-result";
      row.id = `symbol-search-option-${safeId(instrument.instrumentId)}`;
      row.dataset.instrumentId = instrument.instrumentId;
      row.dataset.symbol = instrument.code;
      row.setAttribute("role", "option");
      row.setAttribute("aria-selected", String(index === this.selectedIndex));
      row.classList.toggle("is-current", instrument.instrumentId === currentId);
      row.classList.toggle("is-open", openIds.has(instrument.instrumentId));
      const favorite = document.createElement("button");
      favorite.type = "button";
      favorite.className = "symbol-search-favorite";
      favorite.setAttribute(
        "aria-label",
        `${this.sessionStore.isFavorite(instrument.instrumentId) ? "Remove" : "Add"} ${instrument.code} ${instrument.exchangeDisplayName} ${this.sessionStore.isFavorite(instrument.instrumentId) ? "from" : "to"} favorites`
      );
      favorite.setAttribute("aria-pressed", String(this.sessionStore.isFavorite(instrument.instrumentId)));
      favorite.title = this.sessionStore.isFavorite(instrument.instrumentId)
        ? "Remove from favorites"
        : "Add to favorites";
      favorite.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m12 3 2.8 5.7 6.2.9-4.5 4.4 1.1 6.2-5.6-3-5.6 3 1.1-6.2L3 9.6l6.2-.9L12 3Z"></path></svg>';
      favorite.addEventListener("click", (event) => {
        event.stopPropagation();
        const selected = this.sessionStore.toggleFavorite(instrument.instrumentId);
        favorite.setAttribute("aria-pressed", String(selected));
        favorite.setAttribute(
          "aria-label",
          `${selected ? "Remove" : "Add"} ${instrument.code} ${instrument.exchangeDisplayName} ${selected ? "from" : "to"} favorites`
        );
        favorite.title = selected ? "Remove from favorites" : "Add to favorites";
        row.classList.toggle("is-favorite", selected);
        this.onAnnounce(`${instrument.code} ${selected ? "added to" : "removed from"} favorites.`);
      });
      row.classList.toggle("is-favorite", this.sessionStore.isFavorite(instrument.instrumentId));

      const symbol = document.createElement("strong");
      symbol.className = "symbol-search-code";
      appendHighlightedText(symbol, instrument.code, query);
      const name = document.createElement("span");
      name.className = "symbol-search-name";
      appendHighlightedText(name, instrument.displayName, query);
      const type = document.createElement("span");
      type.className = "symbol-search-type";
      type.textContent = securityTypeSingular(instrument.securityType);
      const exchange = document.createElement("span");
      exchange.className = "symbol-search-exchange";
      if (this.securityType === "Option") {
        const exchangeLabel = document.createElement("span");
        exchangeLabel.textContent =
          instrument.exchangeDisplayName || instrument.exchangeCode || "Exchange unavailable";
        const chainAction = document.createElement("span");
        chainAction.className = "symbol-search-chain-action";
        chainAction.textContent = "View chain";
        chainAction.setAttribute("aria-hidden", "true");
        exchange.append(exchangeLabel, chainAction);
      } else {
        exchange.textContent =
          instrument.exchangeDisplayName || instrument.exchangeCode || "Exchange unavailable";
      }

      row.append(favorite, symbol, name, type, exchange);
      row.addEventListener("pointermove", () => this.setSelectedIndex(index, false));
      row.addEventListener("click", () => this.selectResult(index));
      this.list.append(row);
    }
    this.syncSelection();
  }

  renderFailure() {
    this.list.replaceChildren();
    const failure = document.createElement("div");
    failure.className = "symbol-search-empty symbol-search-failure";
    const title = document.createElement("strong");
    title.textContent = "Symbols could not be loaded";
    const message = document.createElement("span");
    message.textContent = "Check the connection and try the search again.";
    const retry = document.createElement("button");
    retry.type = "button";
    retry.textContent = "Retry";
    retry.addEventListener("click", () => this.performSearch());
    failure.append(title, message, retry);
    this.list.append(failure);
    this.status.textContent = "Symbol search failed. Retry is available.";
    this.onAnnounce("Symbol search failed. Retry is available.");
  }

  setLoading(loading) {
    this.loading.hidden = !loading;
    this.list.setAttribute("aria-busy", String(loading));
    if (loading) {
      this.status.textContent = "Searching symbols.";
    }
  }

  announceResults(total) {
    const count = this.results.length;
    const message = count === 0
      ? "No matching symbols."
      : `${count} symbol${count === 1 ? "" : "s"} shown${total > count ? ` of ${total}` : ""}.`;
    this.status.textContent = message;
    this.onAnnounce(message);
  }

  selectFilter(securityType, focusTarget = null) {
    this.securityType = securityType || null;
    this.selectedIndex = -1;
    this.restoreFilterFocus = focusTarget instanceof HTMLElement;
    this.performSearch();
  }

  handleFilterKeydown(event, index, filters) {
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
    event.preventDefault();
    const next = event.key === "Home"
      ? 0
      : event.key === "End"
        ? filters.length - 1
        : event.key === "ArrowRight"
          ? (index + 1) % filters.length
          : (index - 1 + filters.length) % filters.length;
    const buttons = [...this.filters.querySelectorAll("[role='tab']")];
    this.selectFilter(filters[next].value, buttons[next]);
  }

  handleResultNavigation(event) {
    if (!this.opened) return;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (this.results.length === 0) return;
      const delta = event.key === "ArrowDown" ? 1 : -1;
      const start = this.selectedIndex < 0 ? (delta > 0 ? -1 : 0) : this.selectedIndex;
      this.setSelectedIndex((start + delta + this.results.length) % this.results.length);
      return;
    }
    if ((event.key === "Home" || event.key === "End") && event.currentTarget === this.list) {
      event.preventDefault();
      this.setSelectedIndex(event.key === "Home" ? 0 : this.results.length - 1);
      return;
    }
    if (event.key === "Enter" && this.selectedIndex >= 0) {
      event.preventDefault();
      this.selectResult(this.selectedIndex);
      return;
    }
    if (
      event.currentTarget === this.list
      && event.key.length === 1
      && !event.altKey
      && !event.ctrlKey
      && !event.metaKey
    ) {
      event.preventDefault();
      this.input.focus();
      this.input.value += event.key;
      this.input.dispatchEvent(new Event("input", { bubbles: true }));
    }
  }

  handleDialogKeydown(event) {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      if (this.view === "chain") {
        const focused = this.chainRows.querySelector("tr:focus");
        const strike = focused?.dataset.strike;
        if (strike && this.expandedStrikes.has(strike)) {
          this.toggleStrike(strike, false);
        } else {
          this.showDirectoryView();
        }
      } else {
        this.close();
      }
      return;
    }
    if (event.key !== "Tab") return;
    const focusable = [...this.dialog.querySelectorAll(FOCUSABLE_SELECTOR)]
      .filter((element) => !element.closest("[hidden]"));
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable.at(-1);
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  setSelectedIndex(index, scroll = true) {
    if (this.results.length === 0) {
      this.selectedIndex = -1;
      this.syncSelection();
      return;
    }
    this.selectedIndex = Math.max(0, Math.min(index, this.results.length - 1));
    this.syncSelection(scroll);
  }

  syncSelection(scroll = false) {
    const rows = [...this.list.querySelectorAll("[role='option']")];
    for (const [index, row] of rows.entries()) {
      row.setAttribute("aria-selected", String(index === this.selectedIndex));
    }
    this.syncActiveDescendant();
    if (scroll && this.selectedIndex >= 0) {
      rows[this.selectedIndex]?.scrollIntoView({ block: "nearest" });
    }
  }

  syncActiveDescendant() {
    const row = this.selectedIndex >= 0
      ? this.list.querySelectorAll("[role='option']")[this.selectedIndex]
      : null;
    if (row?.id) {
      this.input.setAttribute("aria-activedescendant", row.id);
      this.list.setAttribute("aria-activedescendant", row.id);
    } else {
      this.input.removeAttribute("aria-activedescendant");
      this.list.removeAttribute("aria-activedescendant");
    }
  }

  async selectResult(index) {
    const instrument = this.results[index];
    if (!instrument) return;
    if (this.securityType === "Option") {
      this.openOptionChain(instrument);
      return;
    }
    let outcome;
    try {
      outcome = await this.selectionHandler(instrument);
    } catch (error) {
      const message = error?.message || "The symbol action could not be completed.";
      this.status.textContent = message;
      this.onAnnounce(message);
      return;
    }
    if (outcome === false || outcome?.accepted === false) {
      const message = outcome?.message || "The active chart is locked to its current symbol.";
      this.status.textContent = message;
      this.onAnnounce(message);
      return;
    }
    this.sessionStore.recordRecent(instrument.instrumentId);
    const focusTarget = outcome?.focusTarget instanceof HTMLElement ? outcome.focusTarget : null;
    this.close({ focusTarget });
  }

  clearQuery() {
    this.input.value = "";
    this.currentQuery = "";
    this.clearButton.hidden = true;
    this.input.focus();
    this.performSearch();
  }

  async openOptionChain(instrument) {
    if (!instrument?.instrumentId) return;
    this.chainUnderlying = instrument;
    this.chainResponse = null;
    this.chainExpirations = [];
    this.view = "chain";
    this.directoryView.hidden = true;
    this.chainView.hidden = false;
    this.element.dataset.view = "option-chain";
    this.chainSymbol.textContent = instrument.code;
    this.chainName.textContent = instrument.displayName;
    this.chainPrice.textContent = "—";
    this.chainChange.textContent = "Quote loading";
    this.chainState.textContent = "Loading expirations…";
    this.chainRows.replaceChildren();
    this.expandedStrikes.clear();
    this.focusedStrikeIndex = -1;
    this.cancelOptionRequests();
    const controller = new AbortController();
    this.expirationRequestController = controller;
    const sequence = ++this.chainRequestSequence;
    this.chainStatus.textContent = `Loading ${instrument.code} option expirations.`;
    this.onAnnounce(this.chainStatus.textContent);
    try {
      const expirations = await this.optionRepository.expirations(
        instrument.instrumentId,
        { signal: controller.signal }
      );
      if (
        !this.opened
        || this.view !== "chain"
        || controller.signal.aborted
        || sequence !== this.chainRequestSequence
      ) return;
      this.chainExpirations = Array.isArray(expirations)
        ? expirations.filter((expiration) => expiration?.date && expiration.isAvailable !== false)
        : [];
      if (this.chainExpirations.length === 0) {
        this.renderChainEmpty(
          "No expirations available",
          "This underlying has no currently available option expirations."
        );
        return;
      }
      this.renderExpirations();
      await this.loadChain();
    } catch (error) {
      if (error?.name === "AbortError" || sequence !== this.chainRequestSequence) return;
      this.renderChainFailure(
        error?.reason === "active_entitlement_required" ? "Real-time options add-on required" : "Expirations could not be loaded",
        optionDataFailureMessage(error, "Return to Options search or retry this underlying.")
      );
    } finally {
      if (this.expirationRequestController === controller) {
        this.expirationRequestController = null;
      }
    }
  }

  showDirectoryView({ restoreState = true } = {}) {
    this.cancelOptionRequests();
    this.view = "directory";
    this.directoryView.hidden = false;
    this.chainView.hidden = true;
    delete this.element.dataset.view;
    if (restoreState) {
      requestAnimationFrame(() => {
        this.syncSelection(true);
        this.input.focus({ preventScroll: true });
      });
      this.onAnnounce("Returned to Options search results.");
    }
  }

  cancelOptionRequests() {
    this.chainRequestSequence += 1;
    this.expirationRequestController?.abort();
    this.chainRequestController?.abort();
    this.expirationRequestController = null;
    this.chainRequestController = null;
    this.cancelOptionDetailRequests();
  }

  cancelOptionDetailRequests() {
    for (const controller of this.optionDetailRequests.values()) controller.abort();
    this.optionDetailRequests.clear();
    this.loadedOptionDetails.clear();
  }

  renderExpirations() {
    const previous = this.chainExpiration.value;
    this.chainExpirationDropdown?.destroy();
    this.chainExpiration.replaceChildren(...this.chainExpirations.map((expiration) => {
      const option = document.createElement("option");
      option.value = expiration.date;
      option.textContent =
        `${formatDate(expiration.date)} · ${expiration.daysToExpiration} DTE · `
        + String(expiration.classification || "Standard");
      return option;
    }));
    if (this.chainExpirations.some((expiration) => expiration.date === previous)) {
      this.chainExpiration.value = previous;
    }
    this.chainExpirationDropdown = enhanceChartDropdown(this.chainExpiration);
  }

  handleExpirationChange() {
    this.chainStatus.textContent =
      `Loading ${formatDate(this.chainExpiration.value)} contracts.`;
    this.onAnnounce(this.chainStatus.textContent);
    this.loadChain();
  }

  handleRangeChange() {
    const custom = this.chainRange.value === "Custom";
    this.chainCustomRange.hidden = !custom;
    if (custom && (!this.chainMinimum.value || !this.chainMaximum.value)) {
      const price = finiteNumber(this.chainResponse?.underlying?.last);
      if (price !== null && price > 0) {
        this.chainMinimum.value = String(Math.max(0.01, Math.floor(price * 0.9)));
        this.chainMaximum.value = String(Math.ceil(price * 1.1));
      }
      return;
    }
    this.loadChain();
  }

  async loadChain({ preserveRows = false } = {}) {
    if (
      !this.opened
      || this.view !== "chain"
      || !this.chainUnderlying
      || !this.chainExpiration.value
    ) return;
    if (
      this.chainRange.value === "Custom"
      && (!this.chainMinimum.value || !this.chainMaximum.value)
    ) return;
    this.chainRequestController?.abort();
    this.cancelOptionDetailRequests();
    const controller = new AbortController();
    this.chainRequestController = controller;
    const sequence = ++this.chainRequestSequence;
    const previousScroll = this.chainScroll.scrollTop;
    const previousFocusedStrike =
      this.chainRows.querySelector("tr:focus")?.dataset.strike || null;
    this.chainRefresh.disabled = true;
    this.chainState.textContent = this.chainResponse
      ? "Refreshing option quotes…"
      : "Loading option chain…";
    this.chainTable.setAttribute("aria-busy", "true");
    try {
      const side = this.chainSide.querySelector("input:checked")?.value || "Both";
      const response = await this.optionRepository.chain({
        underlyingInstrumentId: this.chainUnderlying.instrumentId,
        expiration: this.chainExpiration.value,
        strikeRange: this.chainRange.value,
        strikes: 10,
        side,
        minimumStrike: this.chainRange.value === "Custom"
          ? Number(this.chainMinimum.value)
          : null,
        maximumStrike: this.chainRange.value === "Custom"
          ? Number(this.chainMaximum.value)
          : null,
        signal: controller.signal
      });
      if (
        !this.opened
        || this.view !== "chain"
        || controller.signal.aborted
        || sequence !== this.chainRequestSequence
      ) return;
      this.chainResponse = response;
      this.chainView.dataset.side = side.toLocaleLowerCase();
      this.renderChainHeader(response);
      this.renderChainRows(response.rows || []);
      this.chainState.textContent = "";
      this.chainTable.removeAttribute("aria-busy");
      this.chainStatus.textContent =
        `${response.rows?.length || 0} strikes loaded for ${formatDate(response.expiration?.date)}.`;
      this.onAnnounce(this.chainStatus.textContent);
      if (preserveRows) {
        this.chainScroll.scrollTop = previousScroll;
        const row = previousFocusedStrike
          ? this.chainRows.querySelector(`[data-strike="${cssEscape(previousFocusedStrike)}"]`)
          : null;
        row?.focus({ preventScroll: true });
      } else {
        this.focusAtTheMoney();
      }
    } catch (error) {
      if (error?.name === "AbortError" || sequence !== this.chainRequestSequence) return;
      this.renderChainFailure(
        error?.reason === "active_entitlement_required" ? "Real-time options add-on required" : "Option chain could not be loaded",
        optionDataFailureMessage(error, "The previous chart is unchanged. Retry or select another expiration.")
      );
    } finally {
      if (this.chainRequestController === controller) {
        this.chainRequestController = null;
      }
      this.chainRefresh.disabled = false;
    }
  }

  renderChainHeader(response) {
    const underlying = response.underlying || {};
    const instrument = underlying.instrument || this.chainUnderlying;
    this.chainSymbol.textContent = instrument.code || this.chainUnderlying.code;
    this.chainName.textContent = instrument.displayName || this.chainUnderlying.displayName;
    this.chainPrice.textContent = formatMoney(underlying.last, instrument.currency);
    const change = finiteNumber(underlying.change);
    const percent = finiteNumber(underlying.changePercent);
    const direction = change === null ? null : change > 0 ? "up" : change < 0 ? "down" : "flat";
    this.chainChange.textContent = change === null && percent === null
      ? "—"
      : `${formatSigned(change, 2)} (${formatSignedPercent(percent, 2)})`;
    if (direction) this.chainChange.dataset.direction = direction;
    else delete this.chainChange.dataset.direction;
  }

  renderChainRows(rows) {
    this.chainRows.replaceChildren();
    if (!Array.isArray(rows) || rows.length === 0) {
      this.renderChainEmpty(
        "No contracts in this range",
        "Choose another expiration, strike range, or contract side."
      );
      return;
    }

    for (const [index, rowData] of rows.entries()) {
      const strikeKey = String(rowData.strike);
      const callPrecision = optionPricePrecision(rowData.call?.contract);
      const putPrecision = optionPricePrecision(rowData.put?.contract);
      const row = document.createElement("tr");
      row.className = "option-chain-row";
      row.dataset.strike = strikeKey;
      row.dataset.index = String(index);
      row.tabIndex = index === this.focusedStrikeIndex ? 0 : -1;
      row.setAttribute("aria-expanded", String(this.expandedStrikes.has(strikeKey)));
      row.classList.toggle("is-at-the-money", rowData.isAtTheMoney === true);
      row.classList.toggle("is-expanded", this.expandedStrikes.has(strikeKey));
      row.append(
        createQuoteCell(rowData.call?.quote?.last, "call", callPrecision),
        createQuoteCell(rowData.call?.quote?.bid, "call", callPrecision),
        createQuoteCell(rowData.call?.quote?.ask, "call", callPrecision),
        createStrikeCell(rowData),
        createQuoteCell(rowData.put?.quote?.bid, "put", putPrecision),
        createQuoteCell(rowData.put?.quote?.ask, "put", putPrecision),
        createQuoteCell(rowData.put?.quote?.last, "put", putPrecision)
      );
      row.classList.toggle("call-itm", rowData.callIsInTheMoney === true);
      row.classList.toggle("put-itm", rowData.putIsInTheMoney === true);
      row.addEventListener("click", () => this.toggleStrike(strikeKey));
      row.addEventListener("focus", () => {
        this.focusedStrikeIndex = index;
        this.syncChainFocus();
      });
      this.chainRows.append(row);
      if (this.expandedStrikes.has(strikeKey)) {
        this.chainRows.append(this.createExpandedRow(rowData));
      }
    }
    this.syncChainFocus();
  }

  createExpandedRow(rowData) {
    const detailRow = document.createElement("tr");
    detailRow.className = "option-chain-detail-row";
    detailRow.dataset.detailFor = String(rowData.strike);
    const cell = document.createElement("td");
    cell.colSpan = 7;
    const content = document.createElement("div");
    content.className = "option-chain-details";
    content.append(
      this.createContractDetails(rowData.call, "Call"),
      this.createContractDetails(rowData.put, "Put")
    );
    cell.append(content);
    detailRow.append(cell);
    return detailRow;
  }

  createContractDetails(contractQuote, side) {
    const panel = document.createElement("section");
    panel.className = `option-contract-detail option-contract-${side.toLocaleLowerCase()}`;
    const heading = document.createElement("div");
    heading.className = "option-contract-detail-heading";
    const title = document.createElement("strong");
    title.textContent = `${side} contract`;
    heading.append(title);
    if (!contractQuote?.contract) {
      const unavailable = document.createElement("p");
      unavailable.className = "option-contract-unavailable";
      unavailable.textContent = `${side} contract unavailable at this strike.`;
      panel.append(heading, unavailable);
      return panel;
    }

    const action = document.createElement("button");
    action.type = "button";
    action.textContent = `Open ${side} Chart`;
    action.setAttribute(
      "aria-label",
      `Open ${contractQuote.contract.displayLabel} ${side.toLocaleLowerCase()} chart`
    );
    action.addEventListener("click", (event) => {
      event.stopPropagation();
      this.selectOptionContract(contractQuote.contract);
    });
    heading.append(action);
    const metrics = document.createElement("dl");
    metrics.className = "option-contract-metrics";
    for (const [label, value] of contractMetrics(contractQuote)) {
      const term = document.createElement("dt");
      term.textContent = label;
      const description = document.createElement("dd");
      description.textContent = value;
      metrics.append(term, description);
    }
    panel.append(heading, metrics);
    return panel;
  }

  toggleStrike(strikeKey, next = !this.expandedStrikes.has(strikeKey)) {
    if (next) this.expandedStrikes.add(strikeKey);
    else this.expandedStrikes.delete(strikeKey);
    const rows = this.chainResponse?.rows || [];
    this.renderChainRows(rows);
    const target = this.chainRows.querySelector(
      `.option-chain-row[data-strike="${cssEscape(strikeKey)}"]`
    );
    target?.focus({ preventScroll: true });
    const message = `Strike ${strikeKey} details ${next ? "expanded" : "collapsed"}.`;
    this.chainStatus.textContent = message;
    this.onAnnounce(message);
    if (next) void this.loadStrikeDetails(strikeKey);
  }

  async loadStrikeDetails(strikeKey) {
    const chain = this.chainResponse;
    const row = chain?.rows?.find(item => String(item.strike) === strikeKey);
    if (!row || this.loadedOptionDetails.has(strikeKey) || this.optionDetailRequests.has(strikeKey)) return;
    const controller = new AbortController();
    this.optionDetailRequests.set(strikeKey, controller);
    this.chainStatus.textContent = `Loading details for strike ${strikeKey}…`;
    try {
      const details = await this.optionRepository.rowDetails(chain, row, { signal: controller.signal });
      if (controller.signal.aborted || this.chainResponse !== chain || !this.opened || this.view !== "chain") return;
      chain.rows[chain.rows.indexOf(row)] = details;
      this.loadedOptionDetails.add(strikeKey);
      if (this.expandedStrikes.has(strikeKey)) {
        const scroll = this.chainScroll.scrollTop;
        const focusedStrike = this.chainRows.querySelector("tr:focus")?.dataset.strike;
        this.renderChainRows(chain.rows);
        this.chainScroll.scrollTop = scroll;
        if (focusedStrike) this.chainRows.querySelector(`[data-strike="${cssEscape(focusedStrike)}"]`)?.focus({ preventScroll: true });
        this.chainStatus.textContent = `Strike ${strikeKey} details loaded.`;
        this.onAnnounce(this.chainStatus.textContent);
      }
    } catch (error) {
      if (controller.signal.aborted || this.chainResponse !== chain) return;
      this.chainStatus.textContent = optionDataFailureMessage(error,
        "Option details could not be refreshed. Close and reopen the row to retry.");
      this.onAnnounce(this.chainStatus.textContent);
    } finally {
      if (this.optionDetailRequests.get(strikeKey) === controller) this.optionDetailRequests.delete(strikeKey);
    }
  }

  handleChainKeydown(event) {
    const row = event.target.closest?.(".option-chain-row");
    if (!(row instanceof HTMLElement)) return;
    const rows = [...this.chainRows.querySelectorAll(".option-chain-row")];
    const index = rows.indexOf(row);
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const next = Math.max(
        0,
        Math.min(rows.length - 1, index + (event.key === "ArrowDown" ? 1 : -1))
      );
      rows[next]?.focus();
      this.scrollChainRowIntoView(rows[next]);
    } else if (event.key === "Home" || event.key === "End") {
      event.preventDefault();
      const target = event.key === "Home" ? rows[0] : rows.at(-1);
      target?.focus();
      this.scrollChainRowIntoView(target);
    } else if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
      event.preventDefault();
      this.focusedSide = event.key === "ArrowLeft" ? "Call" : "Put";
      row.dataset.focusedSide = this.focusedSide.toLocaleLowerCase();
      this.chainStatus.textContent = `${this.focusedSide} side selected for strike ${row.dataset.strike}.`;
    } else if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      this.toggleStrike(row.dataset.strike);
    } else if (event.key.toLocaleLowerCase() === "c") {
      event.preventDefault();
      this.openFocusedContract(index, "Call");
    } else if (event.key.toLocaleLowerCase() === "p") {
      event.preventDefault();
      this.openFocusedContract(index, "Put");
    }
  }

  openFocusedContract(index, side) {
    const contract = this.chainResponse?.rows?.[index]?.[side.toLocaleLowerCase()]?.contract;
    if (!contract) {
      this.chainStatus.textContent = `${side} contract is unavailable at this strike.`;
      this.onAnnounce(this.chainStatus.textContent);
      return;
    }
    this.selectOptionContract(contract);
  }

  selectOptionContract(contract) {
    const instrument = normalizeOptionContract(contract);
    if (!instrument) return;
    const outcome = this.onSelect(instrument);
    if (outcome === false || outcome?.accepted === false) {
      const message =
        outcome?.message || "The active chart is locked to its current symbol.";
      this.chainStatus.textContent = message;
      this.onAnnounce(message);
      return;
    }
    this.sessionStore.recordRecent(instrument.instrumentId);
    this.sessionStore.recordRecent(this.chainUnderlying.instrumentId);
    const focusTarget =
      outcome?.focusTarget instanceof HTMLElement ? outcome.focusTarget : null;
    this.close({ focusTarget });
  }

  focusAtTheMoney() {
    const rows = [...this.chainRows.querySelectorAll(".option-chain-row")];
    if (rows.length === 0) return;
    const atTheMoneyIndex = rows.findIndex((row) => row.classList.contains("is-at-the-money"));
    const index = atTheMoneyIndex >= 0
      ? atTheMoneyIndex
      : Math.max(0, Math.min(this.focusedStrikeIndex, rows.length - 1));
    this.focusedStrikeIndex = index;
    this.syncChainFocus();
    const target = rows[index];
    this.scrollChainRowIntoView(target, { center: atTheMoneyIndex >= 0 });
  }

  scrollChainRowIntoView(row, { center = false } = {}) {
    if (!(row instanceof HTMLElement)) return;
    const viewportHeight = this.chainScroll.clientHeight;
    const rowTop = row.offsetTop;
    const rowBottom = rowTop + row.offsetHeight;
    if (center) {
      this.chainScroll.scrollTop = Math.max(
        0,
        rowTop - Math.max(0, (viewportHeight - row.offsetHeight) / 2)
      );
      return;
    }
    const viewportTop = this.chainScroll.scrollTop;
    const viewportBottom = viewportTop + viewportHeight;
    if (rowTop < viewportTop) {
      this.chainScroll.scrollTop = rowTop;
    } else if (rowBottom > viewportBottom) {
      this.chainScroll.scrollTop = rowBottom - viewportHeight;
    }
  }

  syncChainFocus() {
    const rows = [...this.chainRows.querySelectorAll(".option-chain-row")];
    for (const [index, row] of rows.entries()) {
      row.tabIndex = index === this.focusedStrikeIndex ? 0 : -1;
    }
  }

  renderChainEmpty(title, message) {
    this.finishUnavailableChainQuote();
    this.chainRows.replaceChildren();
    this.chainTable.removeAttribute("aria-busy");
    this.chainState.replaceChildren(createStateMessage(title, message));
    this.chainStatus.textContent = `${title}. ${message}`;
    this.onAnnounce(this.chainStatus.textContent);
  }

  renderChainFailure(title, message) {
    this.finishUnavailableChainQuote();
    this.chainTable.removeAttribute("aria-busy");
    const state = createStateMessage(title, message);
    state.classList.add("is-error");
    const retry = document.createElement("button");
    retry.type = "button";
    retry.textContent = "Retry";
    retry.addEventListener("click", () => {
      if (this.chainExpirations.length > 0) this.loadChain({ preserveRows: true });
      else this.openOptionChain(this.chainUnderlying);
    });
    state.append(retry);
    this.chainState.replaceChildren(state);
    this.chainStatus.textContent = `${title}. ${message} Retry is available.`;
    this.onAnnounce(this.chainStatus.textContent);
  }

  finishUnavailableChainQuote() {
    if (this.chainResponse) return;
    this.chainChange.textContent = "—";
  }
}

export function optionDataFailureMessage(error, fallback) {
  if (error?.reason === "provider_browser_unavailable") {
    return "QuoteMedia browser access is unavailable. Check cross-origin access and your market-data session, then retry.";
  }
  if (error?.reason === "active_entitlement_required") {
    return "Enable Real-Time U.S. Options (OPRA) in Market-data setup, then retry.";
  }
  if (error?.reason === "feed_not_mapped") return "An options feed is not configured for this market.";
  if (error?.reason === "capability-disabled") return "Options are disabled for this chart product.";
  switch (error?.status) {
    case 401: return "Your chart session has expired. Sign in again, then reopen Options.";
    case 403: return "Options market-data access was denied. Check your market-data access, then retry.";
    case 404: return "This underlying is no longer available. Return to Options search and select it again.";
    case 429: return "Too many options requests. Wait a moment, then retry.";
    case 502:
    case 503: return "Options data is temporarily unavailable. Try again shortly.";
    default: return fallback;
  }
}

export function directoryFacetTotal(facets = []) {
  return facets.reduce(
    (total, facet) => facet.securityType === "Option" ? total : total + facet.count,
    0
  );
}

export function rankInstruments(
  instruments,
  query,
  {
    favoriteIds = new Set(),
    recentIds = [],
    currentInstrumentId = null,
    openInstrumentIds = []
  } = {}
) {
  const normalizedQuery = normalizeText(query);
  const recentPositions = new Map(recentIds.map((instrumentId, index) => [instrumentId, index]));
  const openIds = new Set(openInstrumentIds);
  return [...instruments].sort((left, right) => {
    const leftRank = matchRank(left, normalizedQuery);
    const rightRank = matchRank(right, normalizedQuery);
    if (leftRank !== rightRank) return leftRank - rightRank;
    const leftBoost = preferenceRank(
      left.instrumentId,
      favoriteIds,
      recentPositions,
      currentInstrumentId,
      openIds
    );
    const rightBoost = preferenceRank(
      right.instrumentId,
      favoriteIds,
      recentPositions,
      currentInstrumentId,
      openIds
    );
    if (leftBoost !== rightBoost) return leftBoost - rightBoost;
    const leftAlphaNumeric = /^[a-z0-9]/i.test(left.code) ? 0 : 1;
    const rightAlphaNumeric = /^[a-z0-9]/i.test(right.code) ? 0 : 1;
    if (leftAlphaNumeric !== rightAlphaNumeric) return leftAlphaNumeric - rightAlphaNumeric;
    if (left.code.length !== right.code.length) return left.code.length - right.code.length;
    return left.code.localeCompare(right.code)
      || left.exchangeCode.localeCompare(right.exchangeCode)
      || left.instrumentId.localeCompare(right.instrumentId);
  });
}

export function matchRank(instrument, normalizedQuery) {
  if (!normalizedQuery) return 0;
  const code = normalizeText(instrument.code);
  const name = normalizeText(instrument.displayName);
  if (code === normalizedQuery) return 0;
  if (code.startsWith(normalizedQuery)) return 100;
  if (code.includes(normalizedQuery)) return 200;
  if (name.startsWith(normalizedQuery)) return 300;
  if (name.split(/[^a-z0-9]+/).some((word) => word.startsWith(normalizedQuery))) return 400;
  if (name.includes(normalizedQuery)) return 500;
  const aliases = Array.isArray(instrument.aliases) ? instrument.aliases : [];
  if (
    normalizeText(instrument.exchangeCode).includes(normalizedQuery)
    || normalizeText(instrument.exchangeDisplayName).includes(normalizedQuery)
    || aliases.some((alias) => normalizeText(alias).includes(normalizedQuery))
  ) return 600;
  return Number.MAX_SAFE_INTEGER;
}

export function normalizeInstrument(value) {
  if (!value || typeof value !== "object") return null;
  const code = String(value.code || "").trim().toUpperCase();
  const instrumentId = String(value.instrumentId || "").trim();
  if (!code || !instrumentId) return null;
  const symbolId = String(value.symbolId || "").trim() || null;
  return {
    symbolId,
    instrumentId,
    code,
    displayName: String(value.displayName || code),
    securityType: String(value.securityType || "Stock"),
    exchangeCode: String(value.exchangeCode || value.exchange || ""),
    exchangeDisplayName: String(value.exchangeDisplayName || value.exchange || ""),
    timeZoneId: String(value.timeZoneId || value.timeZone || "").trim() || null,
    providerSymbol: String(value.providerSymbol || code).trim().toUpperCase(),
    currency: String(value.currency || ""),
    dataAvailability: String(value.dataAvailability || "Historical"),
    aliases: Array.isArray(value.aliases) ? value.aliases.map(String) : []
  };
}

export function normalizeOptionContract(contract) {
  if (!contract || typeof contract !== "object") return null;
  const instrumentId = String(contract.instrumentId || "").trim();
  const displayLabel = String(contract.displayLabel || "").trim();
  if (!instrumentId || !displayLabel) return null;
  return {
    symbolId: null,
    instrumentId,
    code: displayLabel,
    displayName: displayLabel,
    securityType: "Option",
    exchangeCode: String(contract.exchangeCode || ""),
    exchangeDisplayName: String(
      contract.exchangeDisplayName || contract.exchangeCode || "Options"
    ),
    providerSymbol: String(contract.canonicalSymbol || contract.occSymbol || "").trim().toUpperCase(),
    currency: String(contract.currency || "USD"),
    dataAvailability: "Delayed",
    aliases: [
      String(contract.canonicalSymbol || ""),
      String(contract.occSymbol || "")
    ].filter(Boolean),
    optionContract: { ...contract }
  };
}

function preferenceRank(
  instrumentId,
  favoriteIds,
  recentPositions,
  currentInstrumentId,
  openIds
) {
  if (instrumentId === currentInstrumentId) return -400;
  if (favoriteIds.has(instrumentId)) return -300;
  if (recentPositions.has(instrumentId)) return -200 + recentPositions.get(instrumentId);
  if (openIds.has(instrumentId)) return -100;
  return 0;
}

function appendHighlightedText(element, text, query) {
  const source = String(text || "");
  const normalizedQuery = String(query || "").trim();
  if (!normalizedQuery) {
    element.textContent = source;
    return;
  }
  const index = source.toLocaleLowerCase().indexOf(normalizedQuery.toLocaleLowerCase());
  if (index < 0) {
    element.textContent = source;
    return;
  }
  if (index > 0) element.append(document.createTextNode(source.slice(0, index)));
  const mark = document.createElement("mark");
  mark.textContent = source.slice(index, index + normalizedQuery.length);
  element.append(mark);
  if (index + normalizedQuery.length < source.length) {
    element.append(document.createTextNode(source.slice(index + normalizedQuery.length)));
  }
}

function normalizeText(value) {
  return String(value || "").trim().toLocaleUpperCase();
}

function securityTypeLabel(value) {
  return {
    Stock: "Stocks",
    Etf: "ETFs",
    Index: "Indices",
    Future: "Futures",
    Forex: "Forex",
    Crypto: "Crypto",
    Fund: "Funds",
    Option: "Options"
  }[value] || value;
}

function securityTypeSingular(value) {
  return {
    Stock: "Stock",
    Etf: "ETF",
    Index: "Index",
    Future: "Future",
    Forex: "Forex",
    Crypto: "Crypto",
    Fund: "Fund",
    Option: "Option"
  }[value] || value;
}

function createCount(value) {
  const count = document.createElement("span");
  count.className = "symbol-search-filter-count";
  count.textContent = value;
  return count;
}

function createQuoteCell(value, side, precision = 2) {
  const cell = document.createElement("td");
  cell.className = `option-quote-cell option-${side}-cell`;
  cell.textContent = formatNumber(value, precision);
  if (finiteNumber(value) === null) {
    cell.classList.add("is-unavailable");
    cell.setAttribute("aria-label", `${side} quote unavailable`);
  }
  return cell;
}

function createStrikeCell(rowData) {
  const cell = document.createElement("th");
  cell.className = "option-chain-strike";
  cell.scope = "row";
  const value = document.createElement("span");
  value.textContent = formatNumber(rowData.strike, strikePrecision(rowData.strike));
  const expand = document.createElement("span");
  expand.className = "option-chain-expand";
  expand.setAttribute("aria-hidden", "true");
  expand.innerHTML =
    '<svg viewBox="0 0 24 24"><path d="m7 10 5 5 5-5"></path></svg>';
  cell.append(value, expand);
  return cell;
}

function contractMetrics(contractQuote) {
  const contract = contractQuote.contract;
  const quote = contractQuote.quote || {};
  const greeks = quote.greeks || {};
  const pricePrecision = optionPricePrecision(contract);
  return [
    ["Contract", contract.occSymbol || contract.canonicalSymbol],
    ["Expiration", formatDate(contract.expiration)],
    ["Strike", formatNumber(contract.strike, strikePrecision(contract.strike))],
    ["Right", contract.right],
    ["Bid size", formatInteger(quote.bidSize)],
    ["Ask size", formatInteger(quote.askSize)],
    ["Day change", formatSigned(quote.change, pricePrecision)],
    ["Change %", formatSignedPercent(quote.changePercent, 2)],
    [
      "Day range",
      finiteNumber(quote.dayLow) === null && finiteNumber(quote.dayHigh) === null
        ? "—"
        : `${formatNumber(quote.dayLow, pricePrecision)} – `
          + `${formatNumber(quote.dayHigh, pricePrecision)}`
    ],
    ["Volume", formatInteger(quote.volume)],
    ["Open interest", formatInteger(quote.openInterest)],
    ["Implied volatility", formatPercent(quote.impliedVolatility)],
    ["Delta", formatNumber(greeks.delta, 4)],
    ["Gamma", formatNumber(greeks.gamma, 4)],
    ["Theta", formatNumber(greeks.theta, 4)],
    ["Vega", formatNumber(greeks.vega, 4)],
    ["Rho", formatNumber(greeks.rho, 4)],
    ["Intrinsic value", formatNumber(quote.intrinsicValue, pricePrecision)],
    ["Extrinsic value", formatNumber(quote.extrinsicValue, pricePrecision)],
    ["Break-even", formatNumber(quote.breakEvenPrice, pricePrecision)],
    ["Multiplier", formatInteger(contract.multiplier)],
    ["Exercise", contract.exerciseStyle || "—"],
    ["Settlement", contract.settlementType || "—"],
    ["Exchange", contract.exchangeDisplayName || contract.exchangeCode || "—"],
    ["Quote time", quote.quoteTimestampUtc ? formatTime(quote.quoteTimestampUtc) : "—"],
    ["Data", quote.state || "Unavailable"]
  ];
}

function createStateMessage(title, message) {
  const state = document.createElement("div");
  state.className = "option-chain-state-message";
  const strong = document.createElement("strong");
  strong.textContent = title;
  const span = document.createElement("span");
  span.textContent = message;
  state.append(strong, span);
  return state;
}

function formatMoney(value, currency = "USD") {
  const number = finiteNumber(value);
  if (number === null) return "—";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency || "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(number);
  } catch {
    return `$${formatNumber(value, 2)}`;
  }
}

function formatNumber(value, precision = 2) {
  const number = finiteNumber(value);
  if (number === null) return "—";
  return number.toLocaleString(undefined, {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision
  });
}

function formatSigned(value, precision) {
  const number = finiteNumber(value);
  if (number === null) return "—";
  return `${number > 0 ? "+" : ""}${formatNumber(number, precision)}`;
}

function formatSignedPercent(value, precision) {
  const number = finiteNumber(value);
  return number === null ? "—" : `${formatSigned(number, precision)}%`;
}

function formatPercent(value) {
  const number = finiteNumber(value);
  return number === null ? "—" : `${formatNumber(number * 100, 1)}%`;
}

function formatInteger(value) {
  const number = finiteNumber(value);
  return number === null ? "—" : Math.round(number).toLocaleString();
}

function finiteNumber(value) {
  if (value === null || value === undefined || String(value).trim() === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(`${value}T12:00:00Z`);
  if (Number.isNaN(date.valueOf())) return String(value);
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC"
  });
}

function formatTime(value) {
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return "—";
  return date.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit"
  });
}

function strikePrecision(value) {
  return Number.isInteger(Number(value)) ? 0 : 2;
}

function optionPricePrecision(contract) {
  const tickSize = Number(contract?.tickSize);
  if (!Number.isFinite(tickSize) || tickSize <= 0) return 2;
  return Math.min(8, Math.max(0, Math.ceil(-Math.log10(tickSize))));
}

function cssEscape(value) {
  if (globalThis.CSS?.escape) return globalThis.CSS.escape(String(value));
  return String(value).replace(/["\\]/g, "\\$&");
}

function safeId(value) {
  return String(value).replace(/[^a-zA-Z0-9_-]/g, "-");
}

function requiredElement(selector, scope) {
  const element = scope.querySelector(selector);
  if (!(element instanceof HTMLElement)) {
    throw new Error(`Required Symbol Search element ${selector} was not found.`);
  }
  return element;
}
